import { useState, useEffect, useRef } from 'react'
import MultiSelectDropdown from './ui/MultiSelectDropdown'
import cdmData from '../data/cyberDefenseMatrix.json'
import { useAIJobs } from '../../contexts/AIJobsContext'

const assetClassOptions = cdmData.assetClasses.map(a => ({ value: a.id, label: a.name }))
const functionOptions = cdmData.operationalFunctions.map(f => ({ value: f.id, label: f.name }))

function AddEditTechnologyModal({ technology, prefilledCell, onSave, onDelete, onClose }) {
  const isEditing = !!technology
  const { startJob, getJob } = useAIJobs()

  const [name, setName] = useState(technology?.name || '')
  const [vendor, setVendor] = useState(technology?.vendor || '')
  // Multi-category — a single tool can satisfy multiple security categories
  // (e.g., an EDR product that also performs DLP, or an email security tool
  // covering both spam filtering and phishing defense).
  const [categories, setCategories] = useState(
    technology?.categories?.length ? technology.categories
      : technology?.category ? [technology.category]
      : []
  )
  const [assetClasses, setAssetClasses] = useState(
    technology?.assetClasses || (prefilledCell?.assetClass ? [prefilledCell.assetClass] : [])
  )
  const [operationalFunctions, setOperationalFunctions] = useState(
    technology?.operationalFunctions || (prefilledCell?.function ? [prefilledCell.function] : [])
  )
  const [description, setDescription] = useState(technology?.description || '')
  const [usageContext, setUsageContext] = useState(technology?.usageContext || '')
  const [idealClientProfile, setIdealClientProfile] = useState(technology?.idealClientProfile || '')
  const [notes, setNotes] = useState(technology?.notes || '')
  const [saving, setSaving] = useState(false)
  const [aiReasoning, setAiReasoning] = useState(null)
  const [error, setError] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activeJobKey, setActiveJobKey] = useState(null)

  const job = activeJobKey ? getJob(activeJobKey) : null
  const classifying = job?.status === 'running'

  // Apply AI result when it lands. Tracks last applied key so re-runs apply once.
  const lastAppliedKeyRef = useRef(null)
  useEffect(() => {
    if (!activeJobKey || job?.status !== 'success' || lastAppliedKeyRef.current === activeJobKey) return
    lastAppliedKeyRef.current = activeJobKey
    const data = job.result
    if (data.vendor && !vendor.trim()) setVendor(data.vendor)
    // AI may return either categories[] or a singular category. Apply only
    // when the user hasn't already entered values.
    if (categories.length === 0) {
      if (Array.isArray(data.categories) && data.categories.length > 0) {
        setCategories(data.categories.filter(Boolean))
      } else if (data.category) {
        setCategories([data.category])
      }
    }
    if (data.description) setDescription(data.description)
    if (data.idealClientProfile) setIdealClientProfile(data.idealClientProfile)
    if (data.assetClasses) setAssetClasses(data.assetClasses)
    if (data.operationalFunctions) setOperationalFunctions(data.operationalFunctions)
    if (data.reasoning) setAiReasoning(data.reasoning)
  }, [activeJobKey, job?.status, job?.result, vendor, categories])

  useEffect(() => {
    if (job?.status === 'error') setError(job.error || 'AI classification failed')
  }, [job?.status, job?.error])

  // Derive example tools hint based on current selections
  const getExampleHint = () => {
    if (assetClasses.length === 0 || operationalFunctions.length === 0) return null
    const key = `${assetClasses[0]}-${operationalFunctions[0]}`
    const examples = cdmData.exampleTools[key]
    if (!examples || examples.length === 0) return null
    return `e.g. ${examples.join(', ')}`
  }

  const handleAutoAssign = () => {
    if (!name.trim()) return
    setError('')
    setAiReasoning(null)

    // Stable per-tech key so reopening the modal for the same tech surfaces a
    // running or completed classification, but each unique tech triggers fresh work.
    const key = `cdm-classify:${name.trim().toLowerCase()}:${(vendor || '').trim().toLowerCase()}`
    lastAppliedKeyRef.current = null
    setActiveJobKey(key)

    const payload = {
      name: name.trim(),
      vendor: vendor.trim() || null,
      categories,
      description: description.trim() || null,
      usageContext: usageContext.trim() || null
    }

    startJob(key, async () => {
      const res = await fetch('/api/ai/cdm-classify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'AI classification failed')
      return data
    }).catch(err => {
      console.error('[CDM Classify] Failed:', err)
    })
  }

  const canSave = name.trim() && assetClasses.length > 0 && operationalFunctions.length > 0

  const handleSave = async () => {
    if (!canSave) return

    setSaving(true)
    setError('')

    const result = await onSave({
      name: name.trim(),
      vendor: vendor.trim() || null,
      categories,
      description: description.trim() || null,
      usageContext: usageContext.trim() || null,
      idealClientProfile: idealClientProfile || null,
      notes: notes.trim() || null,
      assetClasses,
      operationalFunctions
    })

    setSaving(false)

    if (result) {
      onClose()
    } else {
      setError('Failed to save. Please try again.')
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setSaving(true)
    const result = await onDelete()
    setSaving(false)
    if (result) {
      onClose()
    } else {
      setError('Failed to delete. Please try again.')
    }
  }

  const exampleHint = getExampleHint()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Technology' : 'Add Technology'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={exampleHint || 'e.g. CrowdStrike Falcon'}
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Client Usage Context */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client Usage Context
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
              How does the client use this tool? Include license tier, modules, deployment scope, etc.
            </p>
            <textarea
              value={usageContext}
              onChange={(e) => setUsageContext(e.target.value)}
              placeholder="e.g. Enterprise license, deployed to all endpoints, EDR + threat hunting modules active..."
              rows={2}
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Auto-assign with AI */}
          <div>
            <button
              type="button"
              onClick={handleAutoAssign}
              disabled={!name.trim() || classifying}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-2 border-dashed border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {classifying ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Classifying...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Auto-complete with AI
                </>
              )}
            </button>
            {!name.trim() && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Enter a technology name first</p>
            )}
          </div>

          {/* AI Reasoning */}
          {aiReasoning && (
            <div className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <svg className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-xs text-purple-700 dark:text-purple-300">{aiReasoning}</p>
                <p className="text-xs text-purple-500 dark:text-purple-400 mt-1 italic">Review and adjust selections below to match the client's actual usage.</p>
              </div>
              <button
                onClick={() => setAiReasoning(null)}
                className="text-purple-400 hover:text-purple-600 dark:hover:text-purple-200 flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Vendor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Vendor
            </label>
            <input
              type="text"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Auto-populated by AI or enter manually"
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Ideal Client Profile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ideal Client Profile / Target Market
            </label>
            <textarea
              value={idealClientProfile}
              readOnly
              placeholder="Auto-populated by AI"
              rows={2}
              className="w-full p-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 cursor-default"
            />
          </div>

          {/* Categories — multi-select. A single tool can satisfy more than one. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Categories
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
              Select all categories this tool covers. A single product often satisfies multiple categories.
            </p>
            <MultiSelectDropdown
              options={cdmData.categories.map(cat => ({ value: cat, label: cat }))}
              selectedValues={categories}
              onChange={setCategories}
              placeholder="Select categories..."
            />
          </div>

          {/* Asset Classes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Asset Classes <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
              Which asset types does this tool cover?
            </p>
            <MultiSelectDropdown
              options={assetClassOptions}
              selectedValues={assetClasses}
              onChange={setAssetClasses}
              placeholder="Select asset classes..."
              searchPlaceholder="Search assets..."
              maxDisplayTags={5}
            />
          </div>

          {/* Operational Functions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Operational Functions <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
              What security functions does this tool perform?
            </p>
            <MultiSelectDropdown
              options={functionOptions}
              selectedValues={operationalFunctions}
              onChange={setOperationalFunctions}
              placeholder="Select functions..."
              searchPlaceholder="Search functions..."
              maxDisplayTags={5}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tool/Vendor Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of how this tool is used..."
              rows={2}
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Contract details, license info, observations..."
              rows={2}
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div>
            {isEditing && onDelete && !showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-red-600 dark:text-red-400">Delete this entry?</span>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Yes
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  No
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Technology'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddEditTechnologyModal
